import {
  Action,
  ActionList,
  ControlledTextField,
  FieldLabel,
  FormContainer,
  Icon,
  useFormContainerContext,
} from '@if/mui'
import { Box, Tooltip } from '@mui/material'
import { usePostMerchantReferenceCode } from '@src/data/api/credit-api/credit-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Check, Info, Pencil, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApp: CreditApplication
}

interface FormValues {
  merchantReferenceCode: string
}

interface ReferenceCodeFieldsProps {
  isEditing: boolean
  savedValue: string
  onEdit: () => void
  onExitEdit: () => void
}

const ReferenceCodeFields = ({ isEditing, savedValue, onEdit, onExitEdit }: ReferenceCodeFieldsProps) => {
  const { t } = useTranslation()
  const { methods, submit, canSubmit } = useFormContainerContext<FormValues>()
  const value = methods.watch('merchantReferenceCode')

  const cancel = () => {
    methods.reset({ merchantReferenceCode: savedValue })
    onExitEdit()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void submit('enter')
    } else if (e.key === 'Escape') {
      cancel()
    }
  }

  const actions: Action[] = isEditing
    ? [
        {
          icon: Check,
          label: t('common.save'),
          tone: 'success',
          disabled: !canSubmit,
          onClick: () => void submit('primary'),
        },
        { icon: X, label: t('common.cancel'), tone: 'destructive', onClick: cancel },
      ]
    : [
        {
          icon: value ? Pencil : Plus,
          label: value ? t('common.edit') : t('applicationHeader.tooltips.addMerchantReferenceCode'),
          onClick: onEdit,
        },
      ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FieldLabel>{t('viewCreditApplication.yourReferenceNumber')}</FieldLabel>
        <Tooltip title={t('applicationHeader.tooltips.merchantReferenceCodeInfo')}>
          <span style={{ display: 'inline-flex', cursor: 'help' }}>
            <Icon icon={Info} size="sm" color="action" />
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <ControlledTextField
          control={methods.control}
          name="merchantReferenceCode"
          placeholder={t('applicationHeader.tooltips.addMerchantReferenceCode')}
          onClick={!isEditing ? onEdit : undefined}
          onKeyDown={handleKeyDown}
          helperText={isEditing ? t('applicationHeader.tooltips.maxCharacters', { max: 20 }) : undefined}
          autoFocus={isEditing}
          size="small"
          slotProps={{
            htmlInput: { maxLength: 20, 'aria-label': t('viewCreditApplication.yourReferenceNumber') },
            input: { readOnly: !isEditing },
          }}
          sx={{ width: '25%', minWidth: 160, maxWidth: '100%' }}
        />
        <ActionList actions={actions} variant="icon" menuBelow={false} size="small" />
      </Box>
    </Box>
  )
}

export const MerchantReferenceCodeField = ({ creditApp }: Props) => {
  const [postMerchantReferenceCode] = usePostMerchantReferenceCode()
  const [isEditing, setIsEditing] = useState(false)

  return (
    <FormContainer<FormValues>
      mode="edit"
      hideActions
      useFormSetup={() => ({ defaultValues: { merchantReferenceCode: creditApp.merchantReferenceCode ?? '' } })}
      onSubmit={async ({ merchantReferenceCode }) => {
        await postMerchantReferenceCode({
          creditApplicationId: creditApp.id,
          financingProgramId: creditApp.financingProgramId,
          merchantReferenceCodeDto: { merchantReferenceCode, versionTag: creditApp.versionTag || '' },
        })
        setIsEditing(false)
      }}
    >
      <ReferenceCodeFields
        isEditing={isEditing}
        savedValue={creditApp.merchantReferenceCode ?? ''}
        onEdit={() => setIsEditing(true)}
        onExitEdit={() => setIsEditing(false)}
      />
    </FormContainer>
  )
}
